import Button from '~/shared/components/ButtonsV2';
import DeleteIcon from '~/shared/assets/icons/delete.svg';
import Typography from '~/shared/components/Typography';

import AutoscaleRulesDropdown from './AutoscaleRulesDropdown';
import InputAutocompleteMultiSelect from './InputAutocompleteMultiSelect';
import {
  type AutoscaleRulesBuilderAPI,
  type Condition,
  type TagItem,
  type CondItem,
} from './useAutoscaleRulesBuilder';

export default function AutoscaleRulesBuilder({ builder }: { builder: AutoscaleRulesBuilderAPI }) {
  const {
    ruleConditions,
    autoscaleConditionTypes,
    autoscaleConditionOperators,
    addRuleType,
    removeRuleType,
    updateRuleType,
    addCondition,
    removeCondition,
    updateCondition,
    setRuleValues,
    canAddRuleType,
    canAddCondition,
    disabledTitlesForType,
    disabledTitlesForCondition,
  } = builder;

  return (
    <div className="flex flex-col h-full">
      <Typography variant="H5" fontWeight={500} className="pt-4 mb-2 font-family-outfit ">
        Autoscale volumes that match the following conditions:
      </Typography>
      <div className="flex flex-col gap-4 pb-24">
        {ruleConditions.map((condition) => {
          return (
            <div key={condition.id} className="">
              <div className="flex items-start gap-2">
                <div className="w-[160px]">
                  <AutoscaleRulesDropdown
                    items={autoscaleConditionTypes}
                    selectedItem={condition.type ?? undefined}
                    onSelect={(it) => updateRuleType(condition.id, it as TagItem)}
                    disabledTitles={disabledTitlesForType(condition as Condition)}
                  />
                </div>

                <div className="flex-1 flex flex-col gap-2 ">
                  {condition.conditionOperators.map((conditionOperator, idx) => (
                    <div key={conditionOperator.id} className="flex flex-col gap-2 ">
                      <div className="flex-1 flex gap-2">
                        <div className="w-[160px]">
                          <AutoscaleRulesDropdown
                            items={autoscaleConditionOperators}
                            selectedItem={conditionOperator.condition ?? undefined}
                            onSelect={(it) =>
                              updateCondition(condition.id, conditionOperator.id, it as CondItem)
                            }
                            disabledTitles={disabledTitlesForCondition(
                              condition as Condition,
                              conditionOperator
                            )}
                            disabled={!condition.type}
                          />
                        </div>
                        <div className="flex-1">
                          <InputAutocompleteMultiSelect
                            conditionType={condition?.type?.tag || 'instance'}
                            operator={conditionOperator.condition?.operator}
                            values={conditionOperator.value}
                            onChange={(next) =>
                              setRuleValues(condition.id, conditionOperator.id, next)
                            }
                            disabled={!condition.type || !conditionOperator.condition}
                            placeholder={
                              condition?.type?.tag === 'instance'
                                ? 'Search instance ID'
                                : condition?.type?.tag === 'volume'
                                  ? 'Search (key: value)'
                                  : condition?.type?.tag === 'instance_tag'
                                    ? 'Search tag (key:value)'
                                    : condition?.type?.tag === 'cluster'
                                      ? 'Search cluster name'
                                      : condition?.type?.tag === 'node'
                                        ? 'Search node group name'
                                        : 'Search'
                            }
                          />
                        </div>
                        <button
                          className="px-2 py-3 2xl:py-4 h-fit  group cursor-pointer disabled:cursor-not-allowed"
                          disabled={
                            condition.conditionOperators.length === 1 && ruleConditions.length === 1
                          }
                          onClick={() => {
                            if (condition.conditionOperators.length === 1) {
                              // If this condition type has only one condition operator, delete the whole condition type
                              if (ruleConditions.length > 1) {
                                removeRuleType(condition.id);
                              }
                            } else {
                              // Otherwise, delete just this condition operator.
                              removeCondition(condition.id, conditionOperator.id);
                            }
                          }}
                        >
                          <DeleteIcon className="w-5 h-5 text-gray-500 group-hover:text-red group-disabled:stroke-gray-300" />
                        </button>
                      </div>
                      {idx === condition.conditionOperators.length - 1 && (
                        <Button
                          className="self-start"
                          variant="text"
                          size="md"
                          onClick={() => {
                            if (!conditionOperator.condition) {
                              return;
                            }
                            if (canAddCondition(condition as Condition)) {
                              addCondition(condition.id);
                            }
                          }}
                          disabled={
                            !conditionOperator.condition || !canAddCondition(condition as Condition)
                          }
                        >
                          <span className="text-xl">+</span> And
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        <div>
          <Button
            onClick={addRuleType}
            disabled={
              !canAddRuleType ||
              (ruleConditions.length > 0 && !ruleConditions[ruleConditions.length - 1]?.type)
            }
            variant="third"
          >
            <span className="text-xl">+</span> Add condition
          </Button>
        </div>
      </div>
    </div>
  );
}
